import axios, { type AxiosInstance } from 'axios';

import { COMMANDAPI, getUserData, QUERYAPI } from '../constants/global';
import {
	type IReconDecisionPayload,
	type ReconciliationOutcome,
	type ReconDecisionOutcome,
	type ReconLevel,
} from '../interface/reconciliation';

// Standalone clients for Result Reconciliation. Mirror the other src/lib clients:
// attach the agent's core JWT + PartyId, have NO 401->logout interceptor
// (reconciliation is auxiliary — a failure must never sign the agent out), and
// resolve non-2xx via validateStatus so the caller can classify and fall back.
// Reads go to the Query service; the decision WRITE goes to the Command service
// (CQRS), so each has its own base URL.
const attachAuth = (client: AxiosInstance) => {
	client.interceptors.request.use((config) => {
		const { token, user } = getUserData();
		if (token) config.headers.Authorization = `Bearer ${token}`;
		if (user?.PartyId) config.headers.PartyId = user.PartyId;
		return config;
	});
	client.interceptors.response.use((response) => {
		if (typeof response.data === 'string' && response.data) {
			try {
				response.data = JSON.parse(response.data);
			} catch {
				// Not JSON — leave as-is.
			}
		}
		return response;
	});
	return client;
};

const reconciliationApi = attachAuth(axios.create({ baseURL: QUERYAPI }));
const reconciliationCommandApi = attachAuth(axios.create({ baseURL: COMMANDAPI }));

/**
 * Feature switch. When true, the hook calls the backend
 * `parent-child-reconciliation` endpoint (now shipped) so the aggregation,
 * severity and IReV roll-up are computed server-side with the tunable
 * `Verification:*` thresholds — the canonical, cross-app source of truth (the
 * admin app uses the same endpoint). The response contract is identical
 * (`IReconciliationResult`). If the server call misses (e.g. the api.query image
 * with this route isn't rolled out yet), the hook transparently falls back to
 * client-compute, so flipping this is always safe.
 */
export const RECON_SERVER_ENABLED = true;

export interface IReconciliationRequest {
	electionId: number;
	level: ReconLevel;
	entityCode: string;
	// The expected child roster count (known to the caller from api.core's
	// ResultAnalytics). The read store has no roster, so passing it lets the
	// server gate the verdict to `awaitingCoverage` until every child is in.
	childrenTotal?: number;
	includeIRev?: boolean;
}

/**
 * Fetch a server-computed reconciliation. Only meaningful once
 * RECON_SERVER_ENABLED is true and the endpoint exists.
 */
export const getServerReconciliation = async (req: IReconciliationRequest): Promise<ReconciliationOutcome> => {
	try {
		const res = await reconciliationApi.post(
			'/api/v1/i-elect/Result/parent-child-reconciliation',
			{
				ElectionId: req.electionId,
				Level: req.level,
				EntityCode: req.entityCode,
				ChildrenTotal: req.childrenTotal ?? null,
				ResultSource: 'Agent',
				IncludeIRev: req.includeIRev ?? true,
			},
			{
				validateStatus: () => true,
			}
		);
		return { reachable: true, httpStatus: res.status, body: res.data ?? null };
	} catch {
		return { reachable: false };
	}
};

/**
 * Read-back the persisted agent decision for an entity (Query service). Returns
 * the decision or null. Lets the page reflect a prior acknowledge/resolve/escalate
 * on reload — used on the client-compute path where no server reconciliation runs.
 */
export const getReconciliationDecision = async (
	electionId: number,
	level: ReconLevel,
	entityCode: string
): Promise<ReconDecisionOutcome> => {
	try {
		const res = await reconciliationApi.get('/api/v1/i-elect/Result/reconciliation-decision', {
			params: { electionId, level, entityCode },
			validateStatus: () => true,
		});
		return { reachable: true, httpStatus: res.status, body: res.data ?? null };
	} catch {
		return { reachable: false };
	}
};

/**
 * Persist a reconciliation decision (acknowledge / resolve / escalate). This is a
 * WRITE, so it goes to the Command service (`ResultVerification/reconciliation-decision`),
 * which upserts the durable `reconciliation_decisions` record. Safe to call — it
 * resolves the outcome rather than throwing.
 */
export const postReconciliationDecision = async (payload: IReconDecisionPayload): Promise<ReconDecisionOutcome> => {
	const { user } = getUserData();
	try {
		const res = await reconciliationCommandApi.post(
			'/api/v1/i-elect/ResultVerification/reconciliation-decision',
			{
				ElectionId: payload.electionId,
				Level: payload.level,
				EntityCode: payload.entityCode,
				State: payload.state,
				Note: payload.note ?? '',
				ReportId: payload.reportId ?? null,
				AppUser: user?.UserId ? { Id: Number(user.UserId), Name: user.FullName ?? '' } : null,
			},
			{
				validateStatus: () => true,
			}
		);
		return { reachable: true, httpStatus: res.status, body: res.data ?? null };
	} catch {
		return { reachable: false };
	}
};
